import { MatchConfidence } from '@mews-commander/guest-profile';
import { ChartData, DatavizPalette } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { Stack, StackItem, Typography } from '@optimus-web/core';
import { matchAndMergeTestIds } from '../../../../../constants';
import { CircularProgressChart } from './circularProgressChart';
import { ConfidenceTag } from './confidenceTag';

const getConfidenceTextKey = createHandler<MatchConfidence, keyof typeof TextKey>(
    {
        [MatchConfidence.High]: 'AccountMergeMatchConfidenceHigh',
        [MatchConfidence.Medium]: 'AccountMergeMatchConfidenceMedium',
        [MatchConfidence.Low]: 'AccountMergeMatchConfidenceLow',
        [MatchConfidence.VeryLow]: 'AccountMergeMatchConfidenceVeryLow',
    },
    'AccountMergeMatchConfidenceVeryLow'
);

const getConfidenceValue = createHandler<MatchConfidence, number>(
    {
        [MatchConfidence.High]: 90,
        [MatchConfidence.Medium]: 75,
        [MatchConfidence.Low]: 50,
        [MatchConfidence.VeryLow]: 10,
    },
    0
);

const getConfidenceColor = createHandler<MatchConfidence, DatavizPalette>(
    {
        [MatchConfidence.High]: 'green',
        [MatchConfidence.Medium]: 'blue',
        [MatchConfidence.Low]: 'orange',
        [MatchConfidence.VeryLow]: 'red',
    },
    'red'
);

interface ConfidenceChartProps {
    confidence: MatchConfidence;
}

const getConfidenceChartData = ({ confidence }: ConfidenceChartProps): ChartData[] => [
    {
        id: 'confidenceScore',
        value: getConfidenceValue(confidence),
        color: getConfidenceColor(confidence),
        name: getConfidenceTextKey(confidence),
    },
];

export const ConfidenceChart = ({ confidence }: ConfidenceChartProps) => {
    const data = getConfidenceChartData({ confidence });
    const label = getConfidenceTextKey(confidence);

    return (
        <Stack
            vertical
            spacing="200"
            itemAlignment="center"
            data-test-id={`${matchAndMergeTestIds.confidenceLevel}-${confidence}`}
        >
            <CircularProgressChart
                data={data}
                id="match-confidence-pie-chart"
                total={100}
                minWidth={160}
                strokeWidth={2}
            >
                <Typography textStyle="highlightMedium" align="center" as="div">
                    {lt(label)}
                </Typography>
            </CircularProgressChart>
            <StackItem>
                <ConfidenceTag confidence={confidence} />
            </StackItem>
        </Stack>
    );
};
